import React, { useState } from 'react';
import { X, Copy, Check, Linkedin, FileText, Hash, Target, Zap, LayoutTemplate, Briefcase, MessageSquare } from 'lucide-react';
import type { MissionDetails, GeneratedAdContent } from '../../types';

interface MissionAdGeneratorModalProps {
    isOpen: boolean;
    onClose: () => void;
    missionData: MissionDetails;
    generatedContent: GeneratedAdContent;
}

const MissionAdGeneratorModal: React.FC<MissionAdGeneratorModalProps> = ({
    isOpen,
    onClose,
    missionData,
    generatedContent
}) => {
    const [activeTab, setActiveTab] = useState<'linkedin' | 'jobboard' | 'message'>('linkedin');
    const [copiedSection, setCopiedSection] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleCopy = (text: string, sectionId: string) => {
        navigator.clipboard.writeText(text);
        setCopiedSection(sectionId);
        setTimeout(() => setCopiedSection(null), 2000);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[95vh] md:h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">

                {/* HEADER */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                            <SparklesIcon size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">Mission Ad & Copywriting Hub</h2>
                            <p className="text-xs text-slate-500 font-medium">Génération multi-canaux pour {missionData.client}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* MAIN CONTENT AREA */}
                <div className="flex flex-1 overflow-hidden">

                    {/* LEFT: CONTENT EDITOR (Scrollable) */}
                    <div className="flex-1 flex flex-col min-w-0 bg-slate-50/50">

                        {/* TABS */}
                        <div className="flex items-center gap-1 px-6 pt-6 border-b border-slate-200 bg-white sticky top-0 z-10">
                            <button
                                onClick={() => setActiveTab('linkedin')}
                                className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'linkedin'
                                    ? 'border-[#0077B5] text-[#0077B5]'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                            >
                                <Linkedin size={16} />
                                LinkedIn Strategy
                            </button>
                            <button
                                onClick={() => setActiveTab('jobboard')}
                                className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'jobboard'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                            >
                                <LayoutTemplate size={16} />
                                Job Board
                            </button>
                            <button
                                onClick={() => setActiveTab('message')}
                                className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'message'
                                    ? 'border-emerald-500 text-emerald-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                            >
                                <MessageSquare size={16} />
                                Message
                            </button>
                        </div>

                        {/* TAB CONTENT */}
                        <div className="flex-1 overflow-y-auto p-6">

                            {/* LINKEDIN VIEW */}
                            {activeTab === 'linkedin' && (
                                <div className="space-y-6 max-w-3xl mx-auto">

                                    {/* HOOK */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <Zap size={14} className="text-amber-500" />
                                                The Hook (Attention Grabber)
                                            </h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.ad_hook, 'hook')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'hook' ? <Check size={12} /> : <Copy size={12} />}
                                                {copiedSection === 'hook' ? 'Copié' : 'Copier'}
                                            </button>
                                        </div>
                                        <div className="p-4 bg-amber-50/30">
                                            <p className="text-lg font-bold text-slate-800 leading-snug">
                                                {generatedContent.ad_hook}
                                            </p>
                                        </div>
                                    </div>

                                    {/* FULL POST — structured sections */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <FileText size={14} className="text-blue-500" />
                                                Full Post Content
                                            </h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.linkedin_post, 'post')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'post' ? <Check size={12} /> : <Copy size={12} />}
                                                {copiedSection === 'post' ? 'Copié' : 'Copier tout'}
                                            </button>
                                        </div>
                                        <div className="divide-y divide-slate-100">
                                            {generatedContent.job_board_description.title && (
                                                <div className="px-5 py-4 flex justify-between items-start gap-3">
                                                    <p className="text-base font-bold text-slate-900 leading-snug flex-1">{generatedContent.job_board_description.title}</p>
                                                    <button onClick={() => handleCopy(generatedContent.job_board_description.title, 'post_title')} className="text-slate-400 hover:text-indigo-600 shrink-0 mt-0.5">
                                                        {copiedSection === 'post_title' ? <Check size={11} /> : <Copy size={11} />}
                                                    </button>
                                                </div>
                                            )}
                                            {generatedContent.job_board_description.intro && (
                                                <div className="px-5 py-4 flex justify-between items-start gap-3">
                                                    <p className="text-sm text-slate-700 leading-relaxed flex-1">{generatedContent.job_board_description.intro}</p>
                                                    <button onClick={() => handleCopy(generatedContent.job_board_description.intro, 'post_intro')} className="text-slate-400 hover:text-indigo-600 shrink-0 mt-0.5">
                                                        {copiedSection === 'post_intro' ? <Check size={11} /> : <Copy size={11} />}
                                                    </button>
                                                </div>
                                            )}
                                            {generatedContent.job_board_description.technical_requirements && (
                                                <div className="px-5 py-4 flex justify-between items-start gap-3">
                                                    <div className="flex-1">
                                                        <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-1.5">🔧 Compétences Techniques</p>
                                                        <p className="text-sm text-slate-700 leading-relaxed">{generatedContent.job_board_description.technical_requirements}</p>
                                                    </div>
                                                    <button onClick={() => handleCopy(generatedContent.job_board_description.technical_requirements, 'post_tech')} className="text-slate-400 hover:text-indigo-600 shrink-0 mt-0.5">
                                                        {copiedSection === 'post_tech' ? <Check size={11} /> : <Copy size={11} />}
                                                    </button>
                                                </div>
                                            )}
                                            {generatedContent.job_board_description.soft_skills_mindset && (
                                                <div className="px-5 py-4 flex justify-between items-start gap-3">
                                                    <div className="flex-1">
                                                        <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-1.5">💡 Profil & Mindset</p>
                                                        <p className="text-sm text-slate-700 leading-relaxed">{generatedContent.job_board_description.soft_skills_mindset}</p>
                                                    </div>
                                                    <button onClick={() => handleCopy(generatedContent.job_board_description.soft_skills_mindset, 'post_soft')} className="text-slate-400 hover:text-indigo-600 shrink-0 mt-0.5">
                                                        {copiedSection === 'post_soft' ? <Check size={11} /> : <Copy size={11} />}
                                                    </button>
                                                </div>
                                            )}
                                            {!generatedContent.job_board_description.title && !generatedContent.job_board_description.technical_requirements && (
                                                <div className="p-6">
                                                    <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                                                        {generatedContent.linkedin_post}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* HASHTAGS */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <Hash size={14} className="text-slate-400" />
                                                Hashtags Strategy
                                            </h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.suggested_hashtags.join(' '), 'hashtags')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'hashtags' ? <Check size={12} /> : <Copy size={12} />}
                                                {copiedSection === 'hashtags' ? 'Copié' : 'Copier'}
                                            </button>
                                        </div>
                                        <div className="p-4 flex flex-wrap gap-2">
                                            {generatedContent.suggested_hashtags.map((tag, i) => (
                                                <span key={i} className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium border border-slate-200">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            )}

                            {/* JOB BOARD VIEW */}
                            {activeTab === 'jobboard' && (
                                <div className="space-y-6 max-w-3xl mx-auto">

                                    {/* TITRE */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Titre du Poste</h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.job_board_description.title, 'jb_title')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'jb_title' ? <Check size={12} /> : <Copy size={12} />}
                                                Copier
                                            </button>
                                        </div>
                                        <div className="p-4 text-base font-bold text-slate-900">
                                            {generatedContent.job_board_description.title || missionData.title}
                                        </div>
                                    </div>

                                    {/* INTRO */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Introduction</h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.job_board_description.intro, 'intro')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'intro' ? <Check size={12} /> : <Copy size={12} />}
                                                Copier
                                            </button>
                                        </div>
                                        <div className="p-4 text-sm text-slate-700 leading-relaxed">
                                            {generatedContent.job_board_description.intro}
                                        </div>
                                    </div>

                                    {/* TASKS */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Missions & Responsabilités</h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.job_board_description.tasks.map(t => `• ${t}`).join('\n'), 'tasks')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'tasks' ? <Check size={12} /> : <Copy size={12} />}
                                                Copier
                                            </button>
                                        </div>
                                        <div className="p-4">
                                            <ul className="space-y-2">
                                                {generatedContent.job_board_description.tasks.map((task, idx) => (
                                                    <li key={idx} className="flex gap-2 text-sm text-slate-700">
                                                        <span className="text-indigo-500 font-bold">•</span>
                                                        {task}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* STACK */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stack Technique</h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.job_board_description.stack, 'stack')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'stack' ? <Check size={12} /> : <Copy size={12} />}
                                                Copier
                                            </button>
                                        </div>
                                        <div className="p-4">
                                            <div className="flex flex-wrap gap-2">
                                                {generatedContent.job_board_description.stack.split(',').map((tech, i) => (
                                                    <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-semibold border border-indigo-100">
                                                        {tech.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* PROFIL RECHERCHÉ — Portrait Robot (persona) */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <Target size={14} className="text-indigo-500" />
                                                Profil Recherché — Portrait Robot
                                            </h3>
                                            <button
                                                onClick={() => handleCopy(generatedContent.job_board_description.profile_required, 'profile')}
                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                            >
                                                {copiedSection === 'profile' ? <Check size={12} /> : <Copy size={12} />}
                                                Copier
                                            </button>
                                        </div>
                                        <div className="p-4 text-sm text-slate-700 leading-relaxed italic bg-indigo-50/30">
                                            {generatedContent.job_board_description.profile_required || 'Portrait robot non défini.'}
                                        </div>
                                    </div>

                                </div>
                            )}

                            {/* MESSAGE VIEW (Outreach Toolbox) */}
                            {activeTab === 'message' && (() => {
                                const shortMsg = generatedContent.outreach_message_short || '';
                                const fullMsg = generatedContent.outreach_message_full || '';
                                const isEmpty = !shortMsg && !fullMsg;
                                return (
                                <div className="space-y-6 max-w-3xl mx-auto">
                                    {/* Tip */}
                                    <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                                        <span className="font-bold shrink-0">💡</span>
                                        <span>Remplacez <code className="bg-blue-100 px-1 rounded font-mono">{'{prénom}'}</code> par le prénom du candidat. Utilisez le DM court pour les comptes non-premium (≤300 car.), et l'InMail pour les profils ciblés.</span>
                                    </div>

                                    {isEmpty && (
                                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                                            <span className="font-bold shrink-0">⚠️</span>
                                            <span>Messages non générés. Relancez l'analyse de la mission pour générer les champs <code className="bg-amber-100 px-1 rounded font-mono">outreach_message_short</code> et <code className="bg-amber-100 px-1 rounded font-mono">outreach_message_full</code>.</span>
                                        </div>
                                    )}

                                    {/* Card 1: Message court (DM non-premium) */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <MessageSquare size={14} className="text-emerald-500" />
                                                Message Court — DM Non-Premium
                                            </h3>
                                            <div className="flex items-center gap-3">
                                                <span className={`text-xs font-semibold ${shortMsg.length > 300 ? 'text-red-500' : 'text-emerald-500'}`}>
                                                    {shortMsg.length}/300 car.
                                                </span>
                                                <button
                                                    onClick={() => handleCopy(shortMsg, 'msg_short')}
                                                    className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                                >
                                                    {copiedSection === 'msg_short' ? <Check size={12} /> : <Copy size={12} />}
                                                    {copiedSection === 'msg_short' ? 'Copié !' : 'Copier'}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                                <div className="bg-white px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">LinkedIn DM — Accroche courte</span>
                                                    <Linkedin size={14} className="text-[#0077B5]" />
                                                </div>
                                                <div className="p-4">
                                                    <p className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                                                        {shortMsg || "Message court non généré."}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card 2: Message complet (InMail) */}
                                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <MessageSquare size={14} className="text-[#0077B5]" />
                                                Message Complet — InMail Premium
                                            </h3>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs font-semibold text-slate-400">{fullMsg.length} car.</span>
                                                <button
                                                    onClick={() => handleCopy(fullMsg, 'msg_full')}
                                                    className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                                >
                                                    {copiedSection === 'msg_full' ? <Check size={12} /> : <Copy size={12} />}
                                                    {copiedSection === 'msg_full' ? 'Copié !' : 'Copier'}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                                <div className="bg-white px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Candidat — Approche LinkedIn InMail</span>
                                                    <Linkedin size={14} className="text-[#0077B5]" />
                                                </div>
                                                <div className="p-4">
                                                    <p className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                                                        {fullMsg || "Message complet non généré."}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* RIGHT: CONTEXT SIDEBAR */}
                    <div className="hidden lg:flex w-80 border-l border-slate-200 bg-white flex-col overflow-y-auto">
                        <div className="p-5 border-b border-slate-100">
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Contexte Stratégique</h3>
                            <p className="text-xs text-slate-500">Vérifiez l'alignement avec la cible.</p>
                        </div>

                        <div className="p-5 space-y-6">
                            {/* PERSONA */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <Target size={14} /> Persona Cible
                                </h4>
                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    <p className="text-xs text-slate-700 italic leading-relaxed">
                                        "{missionData.persona || "Non défini"}"
                                    </p>
                                </div>
                            </div>

                            {/* DEAL SUMMARY */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <Briefcase size={14} /> Deal Summary
                                </h4>
                                <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                                    <p className="text-xs text-indigo-900 font-medium leading-relaxed">
                                        {missionData.deal_summary_one_line || "Non disponible"}
                                    </p>
                                </div>
                            </div>

                            {/* STRATEGIC NOTES */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <Zap size={14} /> Notes Stratégiques
                                </h4>
                                <div className="bg-amber-50 p-3 rounded-lg border border-amber-100">
                                    <p className="text-xs text-amber-900 leading-relaxed">
                                        {missionData.strategicContext || "Aucune note stratégique."}
                                    </p>
                                </div>
                            </div>

                            {/* DEAL BREAKERS */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <X size={14} /> Deal Breakers
                                </h4>
                                <ul className="space-y-2">
                                    {missionData.dealBreakers?.map((db, i) => (
                                        <li key={i} className="flex gap-2 text-xs text-slate-600">
                                            <span className="text-red-500 font-bold">•</span>
                                            {db}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

// Helper Icon Component
const SparklesIcon = ({ size, className }: { size: number, className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        <path d="M5 3v4" />
        <path d="M9 3v4" />
        <path d="M3 5h4" />
        <path d="M3 9h4" />
    </svg>
);

export default MissionAdGeneratorModal;
